import { useMemo, useCallback, useRef } from 'react';
import { DataGrid, TinyButton, DateFilter } from '@lendica/components';
import Box from '@mui/material/Box';
import { ColDef, ValueFormatterParams, ICellRendererParams, GridApi } from 'ag-grid-community';
import {
    safeDateFormatter,
    UserTypeLabel,
    FilterConfig,
    createDateFilterConfig,
    applyFiltersFromUrl,
} from '@lendica/utils';
import { AgGridReact } from 'ag-grid-react';

const ACTIVE_SINCE_FILTER_PARAM = 'activeSince';
const EXPIRES_AT_FILTER_PARAM = 'expiresAt';

const FILTER_CONFIGS: FilterConfig[] = [
    { param: ACTIVE_SINCE_FILTER_PARAM, column: 'active_since', type: 'date' },
    { param: EXPIRES_AT_FILTER_PARAM, column: 'expires_at', type: 'date' }
];

export const ACTIVE_MEMBERS_COL_DEFS = [
    {
        field: 'first_name',
        headerName: 'First name',
        minWidth: 120,
    },
    {
        field: 'last_name',
        headerName: 'Last name',
        minWidth: 120,
    },
    {
        field: 'email',
        headerName: 'Email',
        minWidth: 120,
    },
    {
        field: 'role',
        headerName: 'Role',
        minWidth: 120,
    },
    {
        field: 'active_since',
        headerName: 'Active since',
        type: ['date'],
        sort: 'asc' as const,
        sortIndex: 1,
        filter: createDateFilterConfig(ACTIVE_SINCE_FILTER_PARAM, DateFilter),
        minWidth: 120,
    },
];

export const INVITED_MEMBERS_COL_DEFS = [
    {
        field: 'email',
        headerName: 'Email',
        minWidth: 120,
    },
    {
        field: 'role',
        headerName: 'Role',
        minWidth: 120,
    },
    {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
    },
    {
        field: 'expires_at',
        headerName: 'Expires at',
        type: ['date'],
        filter: createDateFilterConfig(EXPIRES_AT_FILTER_PARAM, DateFilter),
        minWidth: 120,
    },
    {
        field: 'actions',
        headerName: 'Actions',
        type: ['actions'],
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        suppressSpanHeaderHeight: true,
        cellStyle: {
            padding: '0px',
        },
        minWidth: 120,
    },
];

export interface ActiveMembersTableRow {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: UserTypeLabel;
    active_since: string;
}

export interface InvitedMembersTableRow {
    id: string;
    email: string;
    role: UserTypeLabel;
    status: string;
    expires_at: string;
}

export interface MembershipTableProps {
    rowData: ActiveMembersTableRow[] | InvitedMembersTableRow[];
    colDefs: ColDef[];
    gridRef?: React.RefObject<AgGridReact>;
    onFilterChanged?: () => void;
    useGridPagination?: boolean;
    paginationPageSize?: number;
    onResendClick?: (params: InvitedMembersTableRow) => void;
    onRevokeClick?: (params: InvitedMembersTableRow) => void;
}

export const MembershipTable = ({
    rowData,
    colDefs,
    gridRef,
    onFilterChanged,
    useGridPagination = true,
    paginationPageSize = 20,
    onResendClick,
    onRevokeClick,
}: MembershipTableProps) => {
    const gridApiRef = useRef<GridApi | null>(null);
    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            filter: true,
        };
    }, []);

    const columnTypes = useMemo(() => {
        return {
            date: {
                valueFormatter: (params: ValueFormatterParams) => {
                    return safeDateFormatter(params.value as string, 'MMM d, yyyy');
                },
            },
            actions: {
                cellRenderer: (params: ICellRendererParams<InvitedMembersTableRow>) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            width: '100%',
                            height: '100%',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <TinyButton
                            variant="outlined"
                            color="secondary"
                            onClick={() => onResendClick?.(params?.data as InvitedMembersTableRow)}
                        >
                            Resend
                        </TinyButton>
                        <TinyButton
                            variant="outlined"
                            color="error"
                            onClick={() => onRevokeClick?.(params?.data as InvitedMembersTableRow)}
                        >
                            Delete
                        </TinyButton>
                    </Box>
                ),
            },
        };
    }, []);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: 'fitGridWidth' as const,
        };
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        applyFiltersFromUrl(params.api, FILTER_CONFIGS);
    }, []);

    return (
        <DataGrid
            ref={gridRef}
            columnDefs={colDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            autoSizeStrategy={autoSizeStrategy}
            pagination={useGridPagination}
            paginationPageSize={paginationPageSize}
            overlayNoRowsTemplate={`<span>No members found.</span>`}
            onFilterChanged={onFilterChanged}
            onGridReady={onGridReady}
        />
    );
};
